import {Injectable} from "@angular/core";
import {Amplify} from "aws-amplify";
import {
    autoSignIn,
    confirmResetPassword,
    confirmSignIn,
    ConfirmSignInOutput,
    confirmSignUp,
    type ConfirmSignUpInput,
    fetchAuthSession,
    fetchUserAttributes,
    getCurrentUser,
    resetPassword,
    ResetPasswordOutput,
    signIn,
    signOut,
    signUp
} from "aws-amplify/auth";
import {UserService} from "./user.service";
import {LoggedInUser} from "../types/user.types";
import {BehaviorSubject} from "rxjs";

type SignUpParameters = {
    password: string;
    email: string;
    phone_number: string;
    address: string,
    name: string,
    c_denumire: string,
    c_cui: string,
    c_adresa: string,
    c_registru_comert: string,
    userId: string
  };

@Injectable({ providedIn: 'root' })
export class CognitoService {
    private readonly  _signedOutEvt: BehaviorSubject<Date> = new BehaviorSubject(null);

    constructor(private userService: UserService) {}

    get signedOutEvt$(){
        return this._signedOutEvt.asObservable();
    }

    public resetSignInError(){
        this._signedOutEvt.next(null);
    }

    configureAWS(awsCfg: any){
        Amplify.configure({Auth: awsCfg});
    }

    async handleSignUp({
        password,
        email,
        phone_number,
        address,
        name,
        c_denumire,
        c_cui,
        c_adresa,
        c_registru_comert,
        userId,
      }: SignUpParameters) {
        try {
          const { isSignUpComplete, nextStep } = await signUp({
            username: email,
            password: password,
            options: {
              userAttributes: {
                email: email,
                phone_number: phone_number, // E.164 number convention,
                address: address,
                name: name,
                'custom:c_denumire': c_denumire,
                'custom:c_cui': c_cui,
                'custom:c_adresa': c_adresa,
                'custom:c_registru_comert': c_registru_comert,
                'custom:userId': userId
              },
              // optional
              autoSignIn: true // or SignInOptions e.g { authFlowType: "USER_SRP_AUTH" }
            }
          });
        } catch (error) {
          await this.handleSignOut();
          console.log('error signing up:', error);
          throw error;
        }
    }

    async handleSignUpConfirmation({
        username,
        confirmationCode
      }: ConfirmSignUpInput) {
        try {
          const { isSignUpComplete, nextStep } = await confirmSignUp({
            username,
            confirmationCode
          });
        } catch (error) {
          await this.handleSignOut();
          console.log('error confirming sign up', error);
          throw error;
        }
      }

    async handleSignIn( username: string, password: string ): Promise<string> {
        try {
          const { isSignedIn, nextStep }: ConfirmSignInOutput  = await signIn({ username: username, password: password });
          if(isSignedIn){
              await this.updateSignInData();
          }
          return (nextStep.signInStep);
        } catch (error) {
            await this.handleSignOut();
          console.log('error signing in', error);
          throw error;
        }
    }

    async updateSignInData(){
        //signed in
        let triesLeft = 3;
        let session = await fetchAuthSession({forceRefresh: true});
        while(!session?.tokens?.accessToken && triesLeft > 0){
            triesLeft--;
            session = await fetchAuthSession({forceRefresh: true});
        }

        if(session?.tokens?.accessToken) {
            this.accessToken = session.tokens.accessToken.toString();

            const userAttributes = await fetchUserAttributes();
            await this.userService.updateLoggedInUser(userAttributes.email, userAttributes.name, userAttributes['custom:userId']);
        }else{
            await this.handleSignOut();
        }
    }

    async confirmSignIn(newPass: string): Promise<ConfirmSignInOutput>{
            return confirmSignIn({challengeResponse: newPass});
    }

    async handleAutoSignIn() {
        try {
          const signInOutput = await autoSignIn();
          // handle sign-in steps
        } catch (error) {
          await this.handleSignOut();
          console.log(error);
          throw error;
        }
      }

    async handleSignOut() {
        try {
          await signOut({ global: true });
          localStorage.removeItem('accessToken');
          Amplify.configure({});
        } catch (error) {
          console.log('error signing out: ', error);
          throw error;
        }
        this._signedOutEvt.next(new Date());
    }

    public clearAmplify(){
        Amplify.configure({});
    }

    async handleResetPassword(username: string) {
      try {
        const output = await resetPassword({ username });
        this.handleResetPasswordNextSteps(output);
      } catch (error) {
        console.log(error);
        throw error;
      }
    }

    handleResetPasswordNextSteps(output: ResetPasswordOutput) {
      const { nextStep } = output;
      switch (nextStep.resetPasswordStep) {
        case 'CONFIRM_RESET_PASSWORD_WITH_CODE':
          const codeDeliveryDetails = nextStep.codeDeliveryDetails;
          console.log(
            `Confirmation code was sent to ${codeDeliveryDetails.deliveryMedium}`
          );
          // Collect the confirmation code from the user and pass to confirmResetPassword.
          break;
        case 'DONE':
          console.log('Successfully reset password.');
          break;
      }
    }

    async handleConfirmResetPassword(username:string, newPassword: string, confirmationCode: string): Promise<void> {
        await confirmResetPassword({username:username, newPassword: newPassword, confirmationCode: confirmationCode});
    }

    async checkLoggedIn(): Promise<boolean> {
      try {
        await getCurrentUser();
        return true;
      }
      catch (err){
        return false;
      }
    }

    set accessToken(token: string) {
      localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }
}
